import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import { orderBy } from 'lodash-es';
import Search from 'sushiweb/Icons/all/Search';
import Download from 'sushiweb/Icons/all/Download';
import Tooltip from 'sushiweb/Tooltip/Tooltip';
import {
  exportCSV,
  getCurrentDate,
  includesOption,
  indexOfOption,
  searchWithName,
  validateEmployeeName,
} from '../../../helpers';
import DataTables from '../../common/components/DataTables/DataTables';
import { employeeHeadColumn } from '../../common/components/DataTables/components/headColumns';
import {
  REFRESH_LABEL,
  applicableFlowTypes,
  currentFlowTypeEnum,
  employeeStatusEnum,
  statusLabels,
} from '../../common/components/constants';
import ErrorPage from '../../common/components/ErrorPage/ErrorPage';
import {
  SearchContainerV2,
  SearchInput,
  StatusFilterAndExportCSVContainer,
  TableContainer,
} from '../../common/styles';
import StatusFilter from '../../common/components/StatusFilter';
import ZTrackingClient from '../../../jumbo/ZTrackingClient';

const EmployeeTable = ({
  currentFlow,
  getAllEmployees,
  fetchingEmployees,
  selectedWorkspace,
  employeesData,
  fetchedEmployees,
  viewOnlyMode,
}) => {
  console.log(viewOnlyMode);
  const keyFields = ['email'];
  const { employees = [] } = employeesData;
  const [loading, setLoading] = useState(true);
  const [currentData, setCurrentData] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, onInputChange] = useState('');
  const [typingTimeout, setTypingTimeout] = useState(0);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [disabledSearch, setDisabledSearch] = useState(false);
  const [filterStatusOptions, setFilterStatusOptions] = useState([
    {
      value: [5],
      label: 'ACTIVE_ONBOARDED',
      disabled: true,
    },
    {
      value: [1],
      label: 'ADDED_ONBOARDING_PENDING',
      disabled: true,
    },
    {
      value: [2],
      label: 'OPTED_IN_ONBOARDING_INCOMPLETE',
      disabled: true,
    },
    {
      value: [3, 4],
      label: 'EXITED',
      disabled: true,
    },
  ]);
  const filterFunc = val => {
    let flag = false;
    currentFilters.forEach(filter => {
      for (let i = 0; i < filter.value.length; i += 1)
        if (val.status === filter.value[i]) flag = true;
    });
    return flag;
  };

  const enableCurrentFilters = availableStatuses => {
    for (let i = 0; i < filterStatusOptions.length; i += 1) {
      for (let j = 0; j < filterStatusOptions[i].value.length; j += 1) {
        if (availableStatuses.includes(filterStatusOptions[i].value[j])) {
          filterStatusOptions[i].disabled = false;
          break;
        } else filterStatusOptions[i].disabled = true;
      }
    }
    setFilterStatusOptions(filterStatusOptions);
  };

  useEffect(() => {
    if (selectedWorkspace && selectedWorkspace.workspace_id)
      getAllEmployees({
        workspaceId: selectedWorkspace.workspace_id,
        flowType: currentFlow,
      });
  }, [selectedWorkspace, currentFlow]);

  useEffect(() => {
    if (!fetchingEmployees) {
      if (employees) {
        employees.sort(
          (a, b) => new Date(b.created_on) - new Date(a.created_on),
        );
        if (employees.length === 0) {
          setDisabledSearch(true);
        }
        if (currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT) {
          for (let i = 0; i < employees.length; i += 1) {
            employees[i].status = employees[i].limit_status;
          }
        }
        setSearchResults(employees);
        setCurrentData(employees.slice(0, 10));
      }
      setLoading(false);
    }
  }, [fetchingEmployees, currentFlow]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    const timeout = setTimeout(() => {
      if (!fetchingEmployees) {
        const arr = searchWithName(employees, search, keyFields);
        const availableStatuses = [];
        arr.forEach(data => {
          if (!availableStatuses.includes(data.status)) {
            availableStatuses.push(data.status);
          }
        });
        enableCurrentFilters(availableStatuses);
        if (currentFilters.length !== 0) {
          const filteredSearchArr = arr.filter(filterFunc);
          setSearchResults(filteredSearchArr);
        } else {
          setSearchResults(arr);
        }
        handlePageChange(1);
      }
    }, 500);

    return () => setTypingTimeout(timeout);
  }, [search, currentFilters, fetchingEmployees]);

  useEffect(() => {
    if (searchResults) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = searchResults.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage, searchResults]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const exportSearchResultCSV = () => {
    const headColumns = [
      { key: 'name_val', value: 'Name' },
      { key: 'email_val', value: 'Email ID' },
      { key: 'group_name_val', value: 'Team name' },
      { key: 'program_name_val', value: 'Meal plan name' },
      { key: 'created_on_val', value: 'Created On' },
      { key: 'status_val', value: 'Status' },
    ];
    const statusLabelledData = [];
    searchResults.forEach(data => {
      const labelledData = {};
      labelledData.name_val = validateEmployeeName(data.name) ? data.name : ' ';
      labelledData.email_val = data.email;
      labelledData.group_name_val = data.group_name;
      labelledData.program_name_val = data.program_name;
      labelledData.created_on_val = data.created_on;
      labelledData.status_val = statusLabels[employeeStatusEnum[data.status]];
      statusLabelledData.push(labelledData);
    });
    exportCSV(
      statusLabelledData,
      `Employees - ${getCurrentDate()}`,
      headColumns,
    );
    const metaData = currentFilters.reduce((acc, filter, index) => {
      acc[`filter_${index}`] = String(filter);
      return acc;
    }, {});

    ZTrackingClient.recordClick({
      ui_source: 'EMPLOYEES',
      component: 'EMPLOYEES_DOWNLOAD',
      meta_data: metaData,
    });
  };
  const downloadCSVStyle = {
    padding: '15px 20px 10px 0px',
    cursor: 'pointer',
  };

  const refreshPage = () => {
    getAllEmployees({
      workspaceId: selectedWorkspace.workspace_id,
      flowType: currentFlow,
    });
  };

  const handleSelect = val => {
    const newCurrentFilters = [...currentFilters];
    if (includesOption(newCurrentFilters, val)) {
      const index = indexOfOption(newCurrentFilters, val);
      newCurrentFilters.splice(index, 1);
    } else {
      newCurrentFilters.push(val);
    }
    setCurrentFilters(newCurrentFilters);
  };

  const clearFilters = () => {
    setCurrentFilters([]);
  };

  const handleSort = (column, sortDirection) => {
    setSearchResults(orderBy(searchResults, column.selector, sortDirection));
  };

  return (
    <>
      <SearchContainerV2>
        <SearchInput
          leftIcon={<Search />}
          innerWidth='100%'
          value={search}
          onChange={onInputChange}
          onFocus={() => setIsSearchClicked(true)}
          onBlur={() => setIsSearchClicked(false)}
          label={
            isSearchClicked || disabledSearch || search?.length ? (
              'Search by employee email ID'
            ) : (
              <span style={{ fontSize: '16px', fontWeight: 300 }}>
                {' '}
                Search by employee email ID
                {' '}
              </span>
            )
          }
          disabled={disabledSearch}
        />
        <StatusFilterAndExportCSVContainer>
          <StatusFilter
            isDisabled={disabledSearch}
            clearFilters={clearFilters}
            currentFilters={currentFilters}
            options={filterStatusOptions}
            handleSelect={handleSelect}
          />
          {disabledSearch ? (
            <Download color='grey' style={downloadCSVStyle} />
          ) : (
            <Tooltip
              position='top'
              label='Download the employee list'
              appearance='light'
              size='big'
              style={{
                fontSize: '12px',
                opacity: 0.8,
                marginRight: '10px',
                marginLeft: '-30px',
                padding: '10px',
              }}
            >
              <Download
                color='grey'
                onClick={exportSearchResultCSV}
                style={downloadCSVStyle}
              />
            </Tooltip>
          )}
        </StatusFilterAndExportCSVContainer>
      </SearchContainerV2>
      <TableContainer>
        {!fetchedEmployees ? (
          <ErrorPage
            showAction
            actionLabel={REFRESH_LABEL}
            clickAction={refreshPage}
          />
        ) : (
          <DataTables
            columns={employeeHeadColumn}
            loading={fetchingEmployees || loading}
            data={currentData}
            pagination
            totalRows={searchResults.length}
            handlePageChange={handlePageChange}
            onSort={handleSort}
            emptyStateTitle='No employees'
          />
        )}
      </TableContainer>
    </>
  );
};

EmployeeTable.propTypes = {
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
  employeesData: PropTypes.objectOf(PropTypes.any),
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
  getAllEmployees: PropTypes.func,
  fetchingEmployees: PropTypes.bool,
  fetchedEmployees: PropTypes.bool,
  viewOnlyMode: PropTypes.bool,
};

EmployeeTable.defaultProps = {
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_PPI,
  employeesData: {},
  selectedWorkspace: {},
  getAllEmployees: _noop,
  fetchingEmployees: true,
  fetchedEmployees: true,
  viewOnlyMode: false,
};

export default EmployeeTable;
